import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { LOCKED_TABLE_FIELD_IDENTIFIERS } from "@utils/constants";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  BucketInformation,
  CreateFileInformation,
  FileUploadAndParse,
  Paragraph,
  ParsingInformation,
  PlaceholderEditor,
  PlaceholderEditorVariant,
  TableBuilderDynamic,
  UploadFileInformation,
  WYSIWYG,
} from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import {
  ApiReportTemplateSectionFieldFieldTypeChoices,
  Maybe,
  ProviderMetadataType,
  ReportSectionFieldsType,
} from "@api/graphql/types-and-hooks";
import { BespokeInputField } from "@components/FieldComponent/BespokeInputField";
import { InputData as CheckboxInputData, CheckboxListField } from "@components/FieldComponent/CheckboxListField";
import { RadioInputField } from "@components/FieldComponent/RadioInputField";
import { SelectField, InputData as SelectInputData } from "@components/FieldComponent/SelectField";
import "@components/FieldComponent/fieldBlockCard.css";
import {
  getFieldAiGenerated,
  getFieldImageSignedUrl,
  getFieldKey,
  getFieldType,
  parseJson,
} from "@components/FieldComponent/utils";
import {
  PlaceholderValues,
  convertDisplayPlaceholdersToKeys,
  convertKeyPlaceholdersToDisplay,
} from "@components/GoalsBank/constants";
import { useFieldReview } from "@components/QaReview/hooks/useFieldReview";
import { ReportFieldAssessmentAppointments } from "@components/ReportField/inputs/ReportFieldAssessmentAppointments";
import { ReportFieldCoordinationOfCare } from "@components/ReportField/inputs/ReportFieldCoordinationOfCare";
import { ReportFieldDsmTable } from "@components/ReportField/inputs/ReportFieldDsmTable";
import { ReportFieldSpecifiersTable } from "@components/ReportField/inputs/ReportFieldSpecifiersTable";
import { TableContentErrors } from "@components/ReportField/inputs/ReportFieldTable";
import { ReportFieldTreatmentOptionTable } from "@components/ReportField/inputs/ReportFieldTreatmentOptionTable";
import { SAMPLE_TABLE_CONTENT, parseTableContent } from "@components/ReportField/inputs/tableContent";
import { useFocusedFieldManager } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { useAdminData } from "@providers/AdminDataProvider";
import { ReportTemplateSectionField } from "@providers/TemplateEditorProvider";

interface FieldBlockCardProps {
  field: ReportTemplateSectionField | ReportSectionFieldsType;
  isSaving: boolean;
  onSave: (content: string) => Promise<unknown>;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };
  S3BucketUrlRetriever: (params: CreateFileInformation) => Promise<BucketInformation>;
  S3BucketUploadCallback: (params: UploadFileInformation) => Promise<void>;
  reportFileUploadCompleteCallback: (fileId: string) => Promise<void>;
  fileParseStatusRetriever: (fileId: string) => Promise<ParsingInformation>;
  removeFileCallback: (fileId: string) => Promise<void>;
  actionButtonMenu?: PopoverMenuItem[];
  /** @deprecated WYSIWYG editMode is now derived from `permissions.canEdit`. */
  editMode?: boolean;
  onFocus?: (fieldId: string) => void;
  isFocused?: boolean;
  isTemplateEditor?: boolean;
  onBlur?: () => void;
  /** @deprecated WYSIWYG always saves on blur — autosave debounce is 200ms. */
  saveOnBlur?: boolean;
  hasCustomOutputTemplate?: boolean;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

function getTableLockState(fieldIdentifier: string | null | undefined): "data-only" | "readonly-columns" {
  return fieldIdentifier && LOCKED_TABLE_FIELD_IDENTIFIERS.includes(fieldIdentifier) ? "data-only" : "readonly-columns";
}

function syncEditorContent(
  content: string | null | undefined,
  initialContentRef: { current: string },
  pendingContentRef: { current: string | null },
  setEditorKey: (fn: (k: number) => number) => void,
): void {
  const newContent = convertKeyPlaceholdersToDisplay(content?.trim() || "");
  if (newContent !== initialContentRef.current && pendingContentRef.current === null) {
    initialContentRef.current = newContent;
    setEditorKey((k) => k + 1);
  }
}

// ─── Field render params ──────────────────────────────────────────────────────

interface FieldRenderParams {
  field: ReportTemplateSectionField | ReportSectionFieldsType;
  fieldType: ReturnType<typeof getFieldType>;
  fieldKey: ReturnType<typeof getFieldKey>;
  fieldAiGenerated: ReturnType<typeof getFieldAiGenerated>;
  fieldImageSignedUrl: ReturnType<typeof getFieldImageSignedUrl>;
  permissions: { canEdit: boolean; canDelete: boolean };
  isSaving: boolean;
  onSave: (content: string) => Promise<unknown>;
  isTemplateEditor: boolean;
  hasCustomOutputTemplate: boolean;
  editorKey: number;
  initialContentRef: { current: string };
  isFocused: boolean;
  isReviewAndSignPage: boolean;
  isAcesUser: boolean;
  providerMetadata: Maybe<ProviderMetadataType> | null | undefined;
  actionButtonMenu?: PopoverMenuItem[];
  handleFocusField: () => void;
  onFocus: () => void;
  handleBlur: () => void;
  handlePlaceholderEditorChange: (v: string) => void;
  handlePlaceholderEditorBlur: React.FocusEventHandler<HTMLDivElement>;
  S3BucketUrlRetriever: FieldBlockCardProps["S3BucketUrlRetriever"];
  S3BucketUploadCallback: FieldBlockCardProps["S3BucketUploadCallback"];
  reportFileUploadCompleteCallback: FieldBlockCardProps["reportFileUploadCompleteCallback"];
  fileParseStatusRetriever: FieldBlockCardProps["fileParseStatusRetriever"];
  removeFileCallback: FieldBlockCardProps["removeFileCallback"];
}

// ─── Field-type renderers ─────────────────────────────────────────────────────

function renderWysiwygField(p: FieldRenderParams): React.ReactElement {
  return p.isTemplateEditor ? (
    <div
      className={clsx("ph-no-capture", p.permissions.canEdit && "mt-2")}
      onFocus={p.handleFocusField}
      onBlur={p.handlePlaceholderEditorBlur}
    >
      {p.permissions.canEdit ? (
        <PlaceholderEditor
          key={p.editorKey}
          variant={PlaceholderEditorVariant.WYSIWYG}
          title=""
          className="bg-white"
          existingContent={p.initialContentRef.current}
          hintText="Type / for dynamic content"
          insertValues={PlaceholderValues}
          readonly={false}
          onChange={p.handlePlaceholderEditorChange}
        />
      ) : (
        <WYSIWYG
          displayOnlyMode={true}
          existingContent={convertKeyPlaceholdersToDisplay(p.field.content || "")}
          onSave={() => Promise.resolve()}
          isSaving={false}
          title=""
          placeholder=""
          aiGenerated={p.fieldAiGenerated}
          onFocus={p.handleFocusField}
          containerClassName="mt-0!"
        />
      )}
    </div>
  ) : (
    <div className={clsx("focus-manager-wysiwyg ph-no-capture", p.isFocused && "focus-manager-focused")}>
      <WYSIWYG
        isSaving={p.isSaving}
        onSave={async ({ content }) => {
          const convertedContent = convertDisplayPlaceholdersToKeys(content);
          await p.onSave(convertedContent);
        }}
        autoSave={true}
        existingContent={p.field.content || ""}
        key={`${p.field.id}-${p.permissions.canEdit}`}
        title={""}
        placeholder={""}
        displayOnlyMode={!p.permissions.canEdit}
        showTitle={false}
        actionMenuItems={p.actionButtonMenu}
        // Pair editMode with the hardcoded saveOnBlur={true} below — the
        // Mosaic WYSIWYG's pre-existing combinations are
        // (saveOnBlur=true, editMode=canEdit) and (saveOnBlur=false,
        // editMode=false). Mixing saveOnBlur=true with editMode=false is
        // untested territory.
        editMode={false}
        aiGenerated={p.fieldAiGenerated}
        onFocus={p.onFocus}
        onBlur={p.handleBlur}
        saveOnBlur
        autoSaveDebounce={200}
        readOnlyTimerDelay={0}
        readOnlyBorder={true}
        showAutoSaveMessage={false}
        containerClassName="mt-0! mb-0!"
      />
    </div>
  );
}

// The Templates FieldEditor renders FieldBlockCard directly, without going through
// ReportFieldTransition/<ReportField/> — so this branch still needs a real
// implementation. It reuses the same ReportField input the report-editing path uses
// (see PROA-2217), just wired by hand instead of through react-hook-form.
function renderDsmTableField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className="ph-no-capture pr-8" tabIndex={-1} onFocus={p.handleFocusField}>
      <ReportFieldDsmTable value={p.field.content} disabled={!p.permissions.canEdit} onChange={p.onSave} />
    </div>
  );
}

// See renderDsmTableField above for why this can't just delegate to <ReportField/>.
function renderSpecifiersTableField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className="ph-no-capture pr-8" tabIndex={-1} onFocus={p.handleFocusField}>
      <ReportFieldSpecifiersTable value={p.field.content} disabled={!p.permissions.canEdit} onChange={p.onSave} />
    </div>
  );
}

// See renderDsmTableField above for why this can't just delegate to <ReportField/>.
function renderTreatmentOptionTableField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className="ph-no-capture pr-8 p-px overflow-hidden mt-2" tabIndex={-1} onFocus={p.handleFocusField}>
      <ReportFieldTreatmentOptionTable
        field={p.field as ReportSectionFieldsType}
        value={p.field.content}
        disabled={!p.permissions.canEdit}
        onChange={p.onSave}
      />
    </div>
  );
}

// See renderDsmTableField above for why this can't just delegate to <ReportField/>.
function renderAssessmentAppointmentsField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className="ph-no-capture pr-8 p-px overflow-visible" tabIndex={-1} onFocus={p.handleFocusField}>
      <ReportFieldAssessmentAppointments
        value={p.field.content}
        disabled={!p.permissions.canEdit}
        onChange={p.onSave}
      />
    </div>
  );
}

// See renderDsmTableField above for why this can't just delegate to <ReportField/>.
function renderCoordinationOfCareField(p: FieldRenderParams): React.ReactElement {
  return (
    <div
      className="ph-no-capture pr-8 p-px overflow-visible"
      style={{ overflow: "visible" }}
      tabIndex={-1}
      onFocus={p.handleFocusField}
    >
      <ReportFieldCoordinationOfCare value={p.field.content} disabled={!p.permissions.canEdit} onChange={p.onSave} />
    </div>
  );
}

function renderTableField(p: FieldRenderParams): React.ReactElement {
  const tableContent = parseTableContent(p.field?.content, SAMPLE_TABLE_CONTENT);
  if (!tableContent.success) {
    return (
      <div className="ph-no-capture pr-8" tabIndex={-1} onFocus={p.handleFocusField}>
        <TableContentErrors issues={tableContent.issues} />
      </div>
    );
  }
  const jsonDatum = tableContent.data;
  const isEmpty = !jsonDatum.headers.length;
  return (
    <div
      className={`${p.permissions.canEdit ? "-mt-4 min-h-56" : "-mt-6"} ph-no-capture pr-8 overflow-x-auto`}
      tabIndex={-1}
      onFocus={p.handleFocusField}
    >
      {isEmpty ? (
        <>
          <div className="text-text-secondary text-sm text-center mt-16 mb-8">
            No existing data. Hover and click the plus icon below to add a table
          </div>
          <Button
            data-testid="add-table-sample"
            onClick={async () => {
              await p.onSave(JSON.stringify(SAMPLE_TABLE_CONTENT));
            }}
            className="w-full"
          >
            <PlusIcon /> Add table sample
          </Button>
        </>
      ) : (
        <TableBuilderDynamic
          key={p.editorKey}
          tableSaving={p.isSaving}
          onSave={async (datum) => {
            await p.onSave(JSON.stringify(datum));
          }}
          tableId={p.field.id}
          jsonDatum={jsonDatum}
          title=""
          displayOnlyMode={!p.permissions.canEdit}
          actionMenuItems={p.actionButtonMenu}
          submenuOpenDirection="left"
          editMode={p.hasCustomOutputTemplate ? getTableLockState(p.fieldKey) : "full"}
          showEditButton={!p.isReviewAndSignPage}
        />
      )}
    </div>
  );
}

function renderImageField(p: FieldRenderParams): React.ReactElement {
  const renderImage = () => (
    <div
      className={`flex justify-center ${p.permissions.canEdit ? "" : "mt-2"} items-center justify-center rounded-lg border border-dashed border-limestone-200 px-6 hover:bg-limestone-50`}
    >
      <div className="rounded-xl">
        <img
          src={p.fieldImageSignedUrl || ""}
          alt="Invalid URL"
          className={`max-w-full max-h-[32rem] object-contain p-2 rounded-xl`}
        />
        {p.permissions.canEdit && (
          <XMarkIcon
            onClick={() => p.onSave("")}
            className="h-6 w-6 absolute top-2 right-2 cursor-pointer rounded-md border border-limestone-200 bg-white p-1 text-text-secondary transition-all hover:bg-red-200"
          />
        )}
      </div>
    </div>
  );

  const renderFileUpload = () => (
    <div className="-my-2">
      <FileUploadAndParse
        key={JSON.stringify(p.field.content)}
        fileKind={p.field.id}
        existingFiles={[]}
        title=""
        singleFileUpload
        disabled={p.isTemplateEditor}
        supportedFileTypes={["png", "jpg", "gif"]}
        maxSize={"100MB"}
        S3BucketUrlRetriever={p.S3BucketUrlRetriever}
        S3BucketUploadCallback={p.S3BucketUploadCallback}
        reportFileUploadCompleteCallback={p.reportFileUploadCompleteCallback}
        fileParseStatusRetriever={p.fileParseStatusRetriever}
        removeFileCallback={p.removeFileCallback}
      />
    </div>
  );

  return (
    <div className={clsx("group relative pr-6")}>
      <div data-testid="image-input-container">
        <div className="relative">
          {p.fieldImageSignedUrl ? renderImage() : p.permissions.canEdit && renderFileUpload()}
        </div>
      </div>
      {p.actionButtonMenu && (
        <div
          data-testid="image-actions-panel"
          className={`group absolute right-2 ${p.fieldImageSignedUrl ? "top-0" : "top-2"}  flex-col gap-y-4 ${p.permissions.canEdit ? "opacity-0 group-hover:opacity-100" : "pointer-events-none opacity-0"} transition-all`}
        >
          <PopoverMenu mode="secondary" menuItems={p.actionButtonMenu} openDirection="to-bottom-left" />
        </div>
      )}
    </div>
  );
}

function renderSimpleTextField(p: FieldRenderParams): React.ReactElement {
  if (p.isTemplateEditor) {
    return (
      <div
        className={clsx("ph-no-capture", p.permissions.canEdit ? "mt-2" : "")}
        onBlur={p.handlePlaceholderEditorBlur}
      >
        {p.permissions.canEdit ? (
          <PlaceholderEditor
            key={p.editorKey}
            title=""
            className="bg-white"
            existingContent={p.initialContentRef.current}
            hintText="Type / for dynamic content"
            insertValues={PlaceholderValues}
            readonly={false}
            onChange={p.handlePlaceholderEditorChange}
          />
        ) : (
          <WYSIWYG
            displayOnlyMode={true}
            existingContent={convertKeyPlaceholdersToDisplay(p.field.content || "")}
            onSave={() => Promise.resolve()}
            isSaving={false}
            title=""
            placeholder=""
            inputContainerClassName="rounded-xl!"
          />
        )}
      </div>
    );
  }
  return (
    <div className={`focus-manager-input ph-no-capture ${p.isFocused ? "focus-manager-focused" : ""}`}>
      <BespokeInputField
        isSaving={p.isSaving}
        onSave={async ({ content }: { content: string }) => {
          const newValue = convertDisplayPlaceholdersToKeys(content);
          await p.onSave(newValue);
        }}
        existingContent={convertKeyPlaceholdersToDisplay(p.field.content || "")}
        placeholder={""}
        inputType="text"
        displayOnlyMode={!p.permissions.canEdit}
        autoSave={true}
        onFocus={p.onFocus}
        showAsFocused={p.isFocused}
        actionMenuItems={p.actionButtonMenu}
        onBlur={p.handleBlur}
        showAutoSaveMessage={false}
        readOnlyTimerDelay={0}
      />
    </div>
  );
}

function renderDateLikeField(p: FieldRenderParams): React.ReactElement {
  const inputType = p.fieldType === ApiReportTemplateSectionFieldFieldTypeChoices.Date ? "date" : "datetime-local";
  return (
    <div className={`focus-manager-input ph-no-capture ${p.isFocused ? "focus-manager-focused" : ""}`}>
      <BespokeInputField
        isSaving={p.isSaving}
        onSave={async ({ content }: { content: string }) => {
          const newValue = convertDisplayPlaceholdersToKeys(content);
          await p.onSave(newValue);
        }}
        existingContent={convertKeyPlaceholdersToDisplay(p.field.content || "")}
        placeholder={""}
        inputType={inputType}
        displayOnlyMode={!p.permissions.canEdit}
        autoSave={true}
        onFocus={p.onFocus}
        showAsFocused={p.isFocused}
        actionMenuItems={p.actionButtonMenu}
        onBlur={p.handleBlur}
        showAutoSaveMessage={false}
        readOnlyTimerDelay={0}
      />
    </div>
  );
}

function renderSelectField(p: FieldRenderParams): React.ReactElement {
  const parsedInputJson: SelectInputData = parseJson(
    p.field.content || '{"options": ["Option 1", "Option 2"], "answer": null}',
  ) ?? {
    options: [],
    answer: null,
  };
  return (
    <div className="ph-no-capture">
      <SelectField
        inputJson={parsedInputJson}
        onChange={async (datum) => {
          await p.onSave(JSON.stringify(datum));
        }}
        onFocus={p.handleFocusField}
        actionButtonMenu={p.actionButtonMenu || []}
        permissions={p.permissions}
        displayOnlyMode={!p.permissions.canEdit}
        onBlur={p.handleBlur}
      />
    </div>
  );
}

function renderCheckboxField(p: FieldRenderParams): React.ReactElement {
  const parsedInputJson: CheckboxInputData = parseJson(
    p.field.content || '{"options": ["Option 1", "Option 2"], "answer": []}',
  ) ?? {
    options: [],
    answer: [],
  };
  return (
    <div className={clsx("ph-no-capture", p.permissions.canEdit ? "-mt-3 -mb-6" : "-mt-4 -mb-8")}>
      <CheckboxListField
        inputJson={parsedInputJson}
        onChange={async (datum) => {
          await p.onSave(JSON.stringify(datum));
        }}
        displayOnlyMode={!p.permissions.canEdit}
        actionButtonMenu={p.actionButtonMenu || []}
        permissions={p.permissions}
        onBlur={p.handleBlur}
        className={clsx("group relative pr-6")}
      />
    </div>
  );
}

function renderRadioField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className={clsx("ph-no-capture", p.permissions.canEdit ? "-mt-1 -mb-10" : "mt-0 -mb-8")}>
      <RadioInputField
        inputJson={
          parseJson(p.field.content || '{"options": ["Option 1", "Option 2"], "answer": ""}') ?? {
            options: [],
            answer: "",
          }
        }
        onChange={async (datum) => {
          await p.onSave(JSON.stringify(datum));
        }}
        displayOnlyMode={!p.permissions.canEdit}
        actionButtonMenu={p.actionButtonMenu || []}
        permissions={p.permissions}
        inputName={""}
        onBlur={p.handleBlur}
      />
    </div>
  );
}

function renderBlankSignatureField(p: FieldRenderParams): React.ReactElement {
  return (
    <div className="group relative pr-6 py-4">
      <div className="flex flex-col items-start">
        <div className="w-64 border-b-2 border-black" />
        {p.field.content && <span className="text-sm text-text-secondary mt-1 ph-no-capture">{p.field.content}</span>}
      </div>
      {p.actionButtonMenu && p.permissions.canEdit && (
        <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-all">
          <PopoverMenu mode="secondary" menuItems={p.actionButtonMenu} openDirection="to-bottom-left" />
        </div>
      )}
    </div>
  );
}

function renderTypedSignatureField(p: FieldRenderParams): React.ReactElement {
  const hasSignature = p.field.content && p.field.content.trim() !== "";
  const isDisplayOnly = !p.permissions.canEdit;

  if (isDisplayOnly && !hasSignature) {
    return (
      <div id="signature-field" className="typed-signature-field py-4">
        <div className="flex flex-col items-start">
          <div className="w-64 border-b-2 border-black" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`typed-signature-field focus-manager-input ph-no-capture ${p.isFocused ? "focus-manager-focused" : ""} bg-limestone-50 rounded-lg p-4 border border-limestone-100`}
    >
      <BespokeInputField
        isSaving={p.isSaving}
        onSave={async ({ content }: { content: string }) => {
          await p.onSave(content);
        }}
        existingContent={p.field.content || ""}
        placeholder="Enter signature"
        inputType="text"
        displayOnlyMode={!p.permissions.canEdit}
        autoSave={true}
        onFocus={p.onFocus}
        showAsFocused={p.isFocused}
        actionMenuItems={p.actionButtonMenu}
        onBlur={p.handleBlur}
        showAutoSaveMessage={false}
        readOnlyTimerDelay={0}
      />
    </div>
  );
}

// ─── Field type → renderer registry ──────────────────────────────────────────

type FieldRenderer = (p: FieldRenderParams) => React.ReactElement;

const FIELD_RENDERERS: Partial<Record<ApiReportTemplateSectionFieldFieldTypeChoices, FieldRenderer>> = {
  [ApiReportTemplateSectionFieldFieldTypeChoices.Wysiwyg]: renderWysiwygField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.DsmTable]: renderDsmTableField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.SpecifiersTable]: renderSpecifiersTableField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.TreatmentOptionTable]: renderTreatmentOptionTableField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.AssessmentAppointments]: renderAssessmentAppointmentsField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.CoordinationOfCare]: renderCoordinationOfCareField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Table]: renderTableField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Image]: renderImageField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.AssessmentImage]: renderImageField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.SimpleText]: renderSimpleTextField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Date]: renderDateLikeField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Datetime]: renderDateLikeField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Select]: renderSelectField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Checkbox]: renderCheckboxField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.Radio]: renderRadioField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.BlankSignatureLine]: renderBlankSignatureField,
  [ApiReportTemplateSectionFieldFieldTypeChoices.TypedSignature]: renderTypedSignatureField,
};

// ─── Component ────────────────────────────────────────────────────────────────

export const FieldBlockCard: React.FC<FieldBlockCardProps> = ({
  field,
  isSaving,
  onSave,
  permissions,
  S3BucketUrlRetriever,
  reportFileUploadCompleteCallback,
  S3BucketUploadCallback,
  fileParseStatusRetriever,
  removeFileCallback,
  actionButtonMenu,
  isTemplateEditor = false,
  hasCustomOutputTemplate = false,
  onBlur,
  // editMode + saveOnBlur are deprecated; left destructured (unused) so the
  // wrapper components can keep passing them without TS errors until they
  // can be cleaned up.
  editMode: _editMode,
  saveOnBlur: _saveOnBlur,
}) => {
  const { pathname } = useLocation();
  const currentRoute = pathname.split("#")?.pop()?.split("/").pop();
  const isReviewAndSignPage = currentRoute === "review-and-sign";
  const { activate } = useFieldReview();
  const { loginProviderDetailsQuery, isAcesUser } = useAdminData();

  const initialContentRef = useRef(convertKeyPlaceholdersToDisplay(field.content?.trim() || ""));
  const pendingContentRef = useRef<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  const provider = loginProviderDetailsQuery.data?.getLoginProvider;
  const providerMetadata = provider?.providermetadata;

  const fieldType = getFieldType(field);
  const fieldKey = getFieldKey(field);
  const fieldAiGenerated = getFieldAiGenerated(field);
  const fieldImageSignedUrl = getFieldImageSignedUrl(field);

  const handlePlaceholderEditorChange = useCallback((newValue: string) => {
    pendingContentRef.current = convertDisplayPlaceholdersToKeys(newValue);
  }, []);

  const handlePlaceholderEditorBlur: React.FocusEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      if (pendingContentRef.current !== null) {
        onSave(pendingContentRef.current);
        pendingContentRef.current = null;
      }
    },
    [onSave],
  );

  const focusManager = useFocusedFieldManager();
  const isFocused = focusManager?.focusedId === field.id;

  useEffect(() => {
    syncEditorContent(field.content, initialContentRef, pendingContentRef, setEditorKey);
  }, [field.content]);

  const handleFocusField = () => activate(field.id);

  const onFocus = () => {
    handleFocusField();
    focusManager?.setFocused(field.id);
  };

  const handleBlur = () => {
    onBlur?.();
    focusManager?.setFocused("");
  };

  const renderParams: FieldRenderParams = {
    field,
    fieldType,
    fieldKey,
    fieldAiGenerated,
    fieldImageSignedUrl,
    permissions,
    isSaving,
    onSave,
    isTemplateEditor,
    hasCustomOutputTemplate,
    editorKey,
    initialContentRef,
    isFocused,
    isReviewAndSignPage,
    isAcesUser,
    providerMetadata: providerMetadata as Maybe<ProviderMetadataType> | null | undefined,
    actionButtonMenu,
    handleFocusField,
    onFocus,
    handleBlur,
    handlePlaceholderEditorChange,
    handlePlaceholderEditorBlur,
    S3BucketUrlRetriever,
    S3BucketUploadCallback,
    reportFileUploadCompleteCallback,
    fileParseStatusRetriever,
    removeFileCallback,
  };

  const renderer = FIELD_RENDERERS[fieldType as ApiReportTemplateSectionFieldFieldTypeChoices];
  if (renderer) return renderer(renderParams);

  return (
    <>
      {permissions.canEdit ? (
        <div className="p-2 border hover:border-gray-500 rounded-md justify-center">
          <div className="rounded-md bg-gray-50"></div>
          <Paragraph displayType="loud" colorType="primary" className="text-center">
            {fieldType} field type is not supported yet.
          </Paragraph>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
